import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useBrandManager } from '@/hooks/useBrandManager';
import { NewsfeedSourceCategory } from '@/utils/newsfeedHelpers';

export const useNewsfeedSourceCategories = () => {
  const { currentBrandId } = useBrandManager();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: categories = [], isLoading, error } = useQuery({
    queryKey: ['newsfeed-source-categories', currentBrandId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('os_newsfeed_sources_categories')
        .select('*')
        .eq('brand_id', currentBrandId)
        .order('name', { ascending: true });

      if (error) throw error;
      return data as NewsfeedSourceCategory[];
    },
    enabled: !!currentBrandId,
  });

  const createMutation = useMutation({
    mutationFn: async (categoryData: { name: string; color: string; description?: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('os_newsfeed_sources_categories')
        .insert({
          ...categoryData,
          brand_id: currentBrandId,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsfeed-source-categories'] });
      toast({
        title: 'Category created successfully',
      });
    },
    onError: (error: Error) => {
      console.error('Error creating category:', error);
      toast({
        title: 'Error creating category',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: { name: string; color: string; description?: string } }) => {
      const { data, error } = await supabase
        .from('os_newsfeed_sources_categories')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsfeed-source-categories'] });
      toast({
        title: 'Category updated successfully',
      });
    },
    onError: (error: Error) => {
      console.error('Error updating category:', error);
      toast({
        title: 'Error updating category',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      // First, remove category from any sources
      await supabase
        .from('os_newsfeed_sources')
        .update({ category_id: null })
        .eq('category_id', id);

      // Then delete the category
      const { error } = await supabase
        .from('os_newsfeed_sources_categories')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsfeed-source-categories'] });
      queryClient.invalidateQueries({ queryKey: ['newsfeed-sources'] });
      toast({
        title: 'Category deleted successfully',
      });
    },
    onError: (error: Error) => {
      console.error('Error deleting category:', error);
      toast({
        title: 'Error deleting category',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    categories,
    isLoading,
    error,
    createCategory: createMutation.mutate,
    updateCategory: updateMutation.mutate,
    deleteCategory: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
